/**
 * 设置页。
 *
 * 这里放的是**会长期生效的东西**：冰箱本身、主题、忌口、过敏、健康目标。
 * 刻意和"每次点菜"分开：忌口漏一次就是事故，不能靠每顿饭现场回忆。
 *
 * 也负责告诉用户"为什么某个入口是灰的"：服务端能力的真实状态直接摊在这里。
 */

import {
	DEFAULT_DIET_PROFILE,
	DIET_GOAL_LABELS,
	DIET_GOALS,
	SPICY_LEVELS,
	SPICY_LEVEL_LABELS,
	type DietGoal,
	type DietProfile,
	type SpicyLevel,
} from '@fridge/shared';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiError, getToken, saveProfile, setToken } from '../api/client';
import { GLYPHS, IconGlyph } from '../components/IconGlyph';
import { HouseholdSettings } from '../components/HouseholdSettings';
import { PageHeader } from '../components/PageHeader';
import { Pressable } from '../components/Pressable';
import { SegmentedControl } from '../components/SegmentedControl';
import { useToast } from '../components/Toast';
import { isSecureContextOk, isVoiceSupported } from '../intake/voice';
import { useAppData } from '../state/app-context';
import { THEME_LABELS, getThemeMode, setThemeMode, type ThemeMode } from '../theme';

export function SettingsPage(): JSX.Element {
	const { prefs, overview, refreshPrefs, refreshAll } = useAppData();
	const toast = useToast();

	const profile: DietProfile = prefs?.profile ?? { ...DEFAULT_DIET_PROFILE, updatedAt: '' };

	const [goals, setGoals] = useState<DietGoal[]>(profile.goals);
	const [avoid, setAvoid] = useState<string[]>(profile.avoid);
	const [avoidInput, setAvoidInput] = useState('');
	const [spicy, setSpicy] = useState<SpicyLevel>(profile.spicy);
	const [maxMinutes, setMaxMinutes] = useState<number | null>(profile.maxMinutes);
	const [saving, setSaving] = useState(false);
	const [themeMode, setThemeModeState] = useState<ThemeMode>('system');
	const [token, setTokenValue] = useState('');

	useEffect(() => {
		setGoals(profile.goals);
		setAvoid(profile.avoid);
		setSpicy(profile.spicy);
		setMaxMinutes(profile.maxMinutes);
	}, [profile.goals, profile.avoid, profile.spicy, profile.maxMinutes]);

	useEffect(() => {
		setThemeModeState(getThemeMode());
		setTokenValue(getToken());
	}, []);

	function toggleGoal(goal: DietGoal): void {
		setGoals((previous) => {
			const has = previous.includes(goal);
			const next = has ? previous.filter((value) => value !== goal) : [...previous, goal];
			return next.length > 0 ? next : ['whatever'];
		});
	}

	function addAvoid(): void {
		const value = avoidInput.trim();
		if (!value || avoid.includes(value)) {
			setAvoidInput('');
			return;
		}
		setAvoid([...avoid, value]);
		setAvoidInput('');
	}

	async function handleSaveProfile(): Promise<void> {
		setSaving(true);
		try {
			await saveProfile({ goals, avoid, spicy, maxMinutes });
			await refreshPrefs();
			toast.show('存好了，以后生成菜谱都照这个来。', 'success');
		} catch (error) {
			toast.show(error instanceof ApiError ? error.message : '保存失败', 'error');
		} finally {
			setSaving(false);
		}
	}

	function handleTheme(mode: ThemeMode): void {
		setThemeModeState(mode);
		setThemeMode(mode);
	}

	async function handleToken(): Promise<void> {
		setToken(token.trim());
		await refreshAll();
		toast.show('口令已更新。', 'success');
	}

	const voiceReady = isVoiceSupported() && isSecureContextOk();

	return (
		<div>
			<PageHeader title="设置" subtitle="冰箱、忌口和健康目标写在这儿，会一直生效" />

			{/* ---------- 冰箱本身 ---------- */}
			<HouseholdSettings />

			{/* ---------- 外观 ---------- */}
			<Section title="外观">
				<SegmentedControl<ThemeMode>
					options={(['system', 'light', 'dark'] as ThemeMode[]).map((mode) => ({
						value: mode,
						label: THEME_LABELS[mode],
					}))}
					value={themeMode}
					onChange={handleTheme}
				/>
				<p className="mt-2.5 text-[12px] leading-relaxed text-label-2">
					跟随系统时会随你的手机设置自动切换。锁定了就一直是那个。
				</p>
			</Section>

			{/* ---------- 吃什么 ---------- */}
			<Section title="健康目标">
				<div className="flex flex-wrap gap-2">
					{DIET_GOALS.map((goal) => (
						<Pressable
							key={goal}
							scale={0.94}
							className={`chip ${goals.includes(goal) ? 'chip-on' : ''}`}
							onClick={() => toggleGoal(goal)}
						>
							{DIET_GOAL_LABELS[goal]}
						</Pressable>
					))}
				</div>
			</Section>

			<Section title="忌口与过敏">
				<p className="mb-2.5 text-[12px] leading-relaxed text-label-2">
					这是硬约束。生成的菜里绝不会出现这些，服务端还会再拦一道。
				</p>
				<div className="flex gap-2">
					<input
						className="field flex-1"
						placeholder="比如 香菜、花生"
						value={avoidInput}
						onChange={(event) => setAvoidInput(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								event.preventDefault();
								addAvoid();
							}
						}}
					/>
					<Pressable className="cta-quiet px-4" onClick={addAvoid}>
						加
					</Pressable>
				</div>
				{avoid.length > 0 ? (
					<div className="mt-2.5 flex flex-wrap gap-2">
						{avoid.map((item) => (
							<Pressable
								key={item}
								scale={0.94}
								className="chip text-danger"
								style={{ background: 'var(--danger-soft)' }}
								onClick={() => setAvoid(avoid.filter((value) => value !== item))}
							>
								{item}
								<IconGlyph glyph={GLYPHS.close} size={11} weight="bold" />
							</Pressable>
						))}
					</div>
				) : null}
			</Section>

			<Section title="辣度">
				<div className="flex flex-wrap gap-2">
					{SPICY_LEVELS.map((level) => (
						<Pressable
							key={level}
							scale={0.94}
							className={`chip ${spicy === level ? 'chip-on' : ''}`}
							onClick={() => setSpicy(level)}
						>
							{SPICY_LEVEL_LABELS[level]}
						</Pressable>
					))}
				</div>
			</Section>

			<Section title="单次做饭时间上限">
				<input
					className="field tnum"
					inputMode="numeric"
					placeholder="留空表示不限"
					value={maxMinutes ?? ''}
					onChange={(event) => {
						const raw = event.target.value.trim();
						setMaxMinutes(raw === '' ? null : Number.parseInt(raw, 10));
					}}
				/>
				<p className="mt-2.5 text-[12px] text-label-2">单位是分钟。不确定就留空。</p>
			</Section>

			<div className="px-4">
				<Pressable className="cta w-full" disabled={saving} onClick={() => void handleSaveProfile()}>
					{saving ? '保存中' : '保存偏好'}
				</Pressable>
			</div>

			{/* ---------- 服务端 ---------- */}
			<Section title="服务端状态">
				<div className="surface divide-y divide-separator overflow-hidden p-0">
					<StatusRow
						label="文本模型"
						hint="生成菜谱、解析语音文本"
						ok={overview?.capabilities.textLlm ?? false}
						detail={overview?.models.text ?? '未配置'}
					/>
					<StatusRow
						label="视觉模型"
						hint="拍小票识别"
						ok={overview?.capabilities.visionLlm ?? false}
						detail={overview?.models.vision ?? '未配置'}
					/>
					<StatusRow
						label="语音识别"
						hint="由手机浏览器承担，不消耗服务端"
						ok={voiceReady}
						detail={voiceReady ? '可用' : '需要 Safari 与 HTTPS'}
					/>
				</div>
				<p className="mt-2.5 text-[12px] leading-relaxed text-label-2">
					文本模型没配时，语音仍能用（降级成文字规则粗拆），但生成菜谱会不可用。
					视觉模型只影响拍照识别，手工录入和语音都不受影响。
				</p>
			</Section>

			{/* ---------- 站点口令（可选的外墙） ---------- */}
			<Section title="站点口令">
				<div className="surface p-3">
					<p className="text-[12px] leading-relaxed text-label-2">
						{overview?.authEnabled
							? '这个站设了一道口令门。它是整站的门，跟上面那些邀请码不是一回事。'
							: '服务端没设站点口令 —— 现在这是推荐状态：每个冰箱有自己的钥匙，新的人凭 6 位邀请码就能进来，不需要先知道口令。'}
					</p>
					<div className="mt-3 flex gap-2">
						<input
							className="field flex-1"
							type="password"
							placeholder="口令"
							value={token}
							onChange={(event) => setTokenValue(event.target.value)}
						/>
						<Pressable className="cta-quiet px-4" onClick={() => void handleToken()}>
							保存
						</Pressable>
					</div>
					<p className="mt-2.5 text-[11.5px] leading-relaxed text-label-2">
						如果你是自己部署的，用服务器上的 rotate-app-token.sh --off 可以把它关掉。
						开着它只会挡住新用户，数据安全靠的是冰箱钥匙。
					</p>
				</div>
			</Section>

			{/* ---------- 手机用法 ---------- */}
			<Section title="手机上怎么用">
				<div className="surface p-3">
					<p className="text-[12.5px] leading-relaxed text-label-2">
						用 Safari 打开本站，点分享，选「添加到主屏幕」。之后点图标就像原生
						App，没有地址栏，也不会被浏览器标签页打断。
					</p>
					<p className="mt-2 text-[12.5px] leading-relaxed text-label-2">
						语音输入只能在 Safari 或主屏图标里用。微信内置浏览器不支持，这是浏览器的限制。
					</p>
				</div>
			</Section>
		</div>
	);
}

/* ------------------------------------------------------------------ */

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
	return (
		<section className="mb-6 px-4">
			<h2 className="mb-2 px-1 text-[12.5px] text-label-2">{title}</h2>
			{children}
		</section>
	);
}

function StatusRow({
	label,
	hint,
	ok,
	detail,
}: {
	label: string;
	hint: string;
	ok: boolean;
	detail: string;
}): JSX.Element {
	return (
		<div className="flex items-center justify-between gap-3 px-3 py-3">
			<div className="min-w-0">
				<div className="flex items-center gap-2">
					<span
						className={`h-2 w-2 shrink-0 rounded-full ${ok ? 'bg-ok' : 'bg-label-3'}`}
						aria-hidden
					/>
					<span className="text-[14px] text-label">{label}</span>
				</div>
				<p className="mt-0.5 pl-4 text-[11.5px] text-label-2">{hint}</p>
			</div>
			<span className="shrink-0 text-[12px] text-label-2">{detail}</span>
		</div>
	);
}
